/**
 * ErrorBoundary Component
 *
 * Global error boundary that catches rendering errors anywhere in the
 * subtree and displays the editorial 500 surface (`info-pages.html` V2)
 * via the shared `InfoFallbackShell`. Two recovery affordances: retry
 * (rerenders the subtree by clearing the boundary's error state) and
 * go-home (hard navigates to /).
 *
 * Rendering the dev-only diagnostic panel (error.message + componentStack)
 * is the one place we deliberately step OUTSIDE the editorial shell —
 * a debug surface only ever visible when `import.meta.env.DEV`, anchored
 * absolutely at the bottom of the viewport so it doesn't fight the hero.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from '@/core';
import { InfoFallbackShell } from '@/core/pages/info/InfoFallbackShell';
import { reportError } from '@/core/observability/errorReporter';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

function ErrorFallbackUI({
  error,
  errorInfo,
  onRetry,
  onGoHome,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
  onGoHome: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <InfoFallbackShell
        tone="ocean"
        titleId="error-boundary-title"
        statusCode="500"
        subLabel={t('errors.serverError.subLabel', 'HTTP · Server error')}
        kicker={t('errors.serverError.kicker', 'Frontend boundary')}
        tag={t('errors.serverError.tag', 'Unhandled exception')}
        titleA={t('errors.serverError.titleA', 'Something')}
        titleEm={t('errors.serverError.titleEm', 'broke')}
        titleB={t('errors.serverError.titleB', 'on our side.')}
        body={t(
          'errors.serverError.body',
          'An unexpected error caught the rendering layer. Try again — most rendering issues clear on retry. If it keeps happening, take a screenshot and let your manager know.',
        )}
        primaryAction={{
          label: t('errors.serverError.actions.retry', 'Try again'),
          onClick: onRetry,
          iconSide: 'left',
        }}
        secondaryAction={{
          label: t('errors.serverError.actions.goHome', 'Take me home'),
          onClick: onGoHome,
          iconSide: 'right',
        }}
      />

      {/* Dev-only diagnostic. Anchored to the bottom of the viewport so
          it doesn't fight the editorial hero above. Production users
          never see this. */}
      {import.meta.env.DEV && error ? (
        <div className="pointer-events-auto fixed inset-x-4 bottom-4 z-20 max-h-[40vh] overflow-auto rounded-lg border border-destructive/30 bg-card/95 p-4 text-[12px] shadow-xl backdrop-blur">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-destructive">
            {error.name || 'Error'}
          </p>
          <p className="mt-1 font-mono text-[12px] text-foreground">{error.message}</p>
          {errorInfo?.componentStack ? (
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-[1.45] text-muted-foreground">
              {errorInfo.componentStack}
            </pre>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Audit deck-P1-3: forward uncaught render errors to Sentry. The
    // boundary previously only emitted to console.error, which means
    // every production render-time crash was effectively invisible to
    // observability. The Sentry SDK is initialised in main.tsx before
    // <App /> mounts; when the DSN env var is blank (e.g. dev) the
    // call is a no-op so this is safe in every environment.
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack ?? '',
        },
      },
      tags: {
        boundary: 'global-error-boundary',
      },
    });

    // Tap, don't swallow — also forward to the in-house sink. Sentry
    // already captured; this is the parallel path that lands in the
    // shaka-admin inbox + powers the AI-review flow.
    reportError(error, {
      source: 'error-boundary',
      componentStack: errorInfo.componentStack ?? '',
    });

    // Keep the dev-time console log so local debugging is unchanged.
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Return custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
